/**
 * Фабрика ключей React Query.
 *
 * Ключ, собранный строкой в компоненте, — это ключ, о котором никто больше не
 * знает. Отсюда два наблюдаемых дефекта: один и тот же URL уходит двумя
 * параллельными запросами (ключи разошлись, дедупликация не сработала) и
 * «нужен рефреш страницы» после мутации (инвалидировали один ключ из трёх).
 *
 * Правило: ключ приходит отсюда. Массив-литерал в компоненте — повод завести
 * здесь метод, а не написать ещё один вариант написания. Уровни ключа идут от
 * общего к частному, поэтому `invalidateQueries({ queryKey: tournamentKeys.detail(id) })`
 * накрывает и вложенные разделы турнира.
 *
 * KAN-991.
 */

/** Идентификатор турнира: в URL бывает и slug, и uuid — ключ хранит как есть. */
type TournamentId = string;

export const tournamentKeys = {
  all: ["tournaments"] as const,
  lists: () => [...tournamentKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...tournamentKeys.lists(), filters ?? {}] as const,

  /** Турнир целиком: карточка, настройки, статус. */
  detail: (id: TournamentId) => [...tournamentKeys.all, id] as const,
  participants: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "participants"] as const,
  results: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "results"] as const,
  streams: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "streams"] as const,
  rulebook: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "rulebook"] as const,
  moderators: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "moderators"] as const,
  permissions: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "permissions"] as const,
  audit: (id: TournamentId, params?: Record<string, unknown>) =>
    [...tournamentKeys.detail(id), "audit", params ?? {}] as const,
  teams: (id: TournamentId) => [...tournamentKeys.detail(id), "teams"] as const,
  predictionsSheet: (id: TournamentId) =>
    [...tournamentKeys.detail(id), "predictions-sheet"] as const,
} as const;

export const archiveKeys = {
  all: ["tournament-archives"] as const,
  /** Список архивов на странице турниров (пагинация внизу). */
  list: (pageSize: number) => [...archiveKeys.all, "list", pageSize] as const,
  /** Архив конкретного турнира — тот самый ключ, который раньше расходился. */
  detail: (id: TournamentId) => [...archiveKeys.all, id] as const,
  teamsWithRosters: (id: TournamentId) =>
    [...archiveKeys.detail(id), "teams", "withRosters"] as const,
  snapshot: (id: TournamentId) =>
    [...archiveKeys.detail(id), "snapshot"] as const,
} as const;
